import { useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { z } from "zod";
import { useApi } from "../api/hooks";
import { useSession } from "../auth/Session";
import { JournalClient } from "./JournalClient";
import { DocumentsClient } from "./DocumentsClient";
import { StockClient, VentesClient } from "./StockVentesClient";
import { EtatChargement } from "../composants/Avis";
import { Tableau } from "../composants/Tableau";

const ONGLETS = [
  { cle: "documents", titre: "Documents", module: "documents" },
  { cle: "journal", titre: "Journal général", module: "journal" },
  { cle: "stock", titre: "Stock / Inventaire", module: "stock" },
  { cle: "ventes", titre: "Ventes", module: "ventes" },
  { cle: "equipe", titre: "Collaborateurs", module: "equipe" },
  { cle: "transmissions", titre: "Transmissions", module: "documents" },
] as const;

const TransmissionsSchema = z.array(
  z.object({
    id: z.string(),
    annee: z.number(),
    mois: z.number(),
    scelleeLe: z.string(),
    scelleePar: z.string(),
    empreinteManifeste: z.string(),
    nbReeditions: z.number(),
    nbPieces: z.number(),
  }),
);

/**
 * §5.B — Portail client.
 *
 * Les modules non cochés par l'administration sont MASQUÉS.
 * Un collaborateur délégué est forcé sur ['documents','journal'] et ne peut pas
 * gérer les autres collaborateurs.
 */
export function PortailClient() {
  const { onglet = "documents", dossierId: dossierUrl } = useParams();
  const { session, deconnecter } = useSession();

  const estStaff = session?.role === "staff" || session?.role === "admin";

  // ⛔ POUR UN CLIENT, L'IDENTIFIANT VIENT DE LA SESSION, JAMAIS DE L'URL.
  //
  // Un client qui changerait le GUID dans la barre d'adresse lirait le dossier
  // d'un autre. Le serveur le refuserait — le cloisonnement est verifie sur
  // chaque route — mais faire dependre le front d'un refus serveur, c'est
  // n'avoir qu'UNE barriere la ou on croit en avoir deux.
  //
  // Un collaborateur MDC, lui, a le droit de voir tous les dossiers : c'est
  // deja la regle du serveur (`AccesAutorise` : EstStaff || le sien). Il entre
  // donc avec SON jeton et sous SON nom — aucune session client n'est fabriquee,
  // et toute action qu'il ferait ici porte SA signature au journal.
  const dossierId = (estStaff && dossierUrl ? dossierUrl : session?.dossierId) ?? null;

  // Le prefixe des onglets : le collaborateur navigue dans l'apercu d'un dossier
  // precis, le client dans le sien.
  const racine = estStaff && dossierUrl ? `/portail/apercu/${dossierUrl}` : "/portail";

  // Un délégué ne voit que documents et journal, quels que soient les modules
  // activés pour le dossier.
  const visibles =
    session?.role === "delegue"
      ? ONGLETS.filter((o) => o.cle === "documents" || o.cle === "journal")
      : ONGLETS;

  const actif = visibles.find((o) => o.cle === onglet) ?? visibles[0];

  // Un porteur de lien client SANS dossier rattaché est une session incohérente :
  // on le dit, plutôt que de laisser chaque écran échouer en 404.
  if (dossierId === null) {
    return (
      <main className="portail">
        <header className="entete">
          <h1>{session?.nom}</h1>
          <button type="button" onClick={deconnecter}>Quitter</button>
        </header>
        <section className="contenu">
          <p className="alerte" role="alert">
            Cette session n'est rattachée à aucun dossier. Le lien d'accès est
            probablement périmé : demandez-en un nouveau au cabinet.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="portail">
      <header className="entete">
        <div>
          <h1>{session?.nom}</h1>
          {session?.role === "delegue" && (
            <span className="portail__role">Collaborateur délégué</span>
          )}
        </div>
        {estStaff ? (
          <Link className="bouton-creux" to="/dossiers">
            Retour aux dossiers
          </Link>
        ) : (
          <button type="button" onClick={deconnecter}>Quitter</button>
        )}
      </header>

      {/* ⛔ LE BANDEAU N'EST PAS DECORATIF.
          Cet ecran est la copie conforme de celui du client. Sans un rappel
          permanent, un collaborateur croit lire SON ecran, prend une capture
          pour un echange avec le client, ou pire : croit qu'une piece manque
          alors qu'il regarde un autre dossier. Il dit aussi la seule chose qui
          compte juridiquement — ce qu'il ferait ici porte SON nom. */}
      {estStaff && dossierUrl && (
        <p className="portail__apercu">
          Portail du client, vu depuis l'espace collaborateurs. Vous y êtes sous
          votre propre identité : toute action porte <strong>votre signature</strong>{" "}
          au journal, jamais celle du client.
        </p>
      )}

      <nav className="onglets">
        {visibles.map((o) => (
          <NavLink key={o.cle} to={`${racine}/${o.cle}`}>
            {o.titre}
          </NavLink>
        ))}
      </nav>

      <section className="contenu">
        <h2>{actif.titre}</h2>

        {actif.cle === "documents" && <DocumentsClient dossierId={dossierId} />}
        {actif.cle === "journal" && <JournalClient dossierId={dossierId} />}
        {actif.cle === "stock" && <StockClient dossierId={dossierId} />}
        {actif.cle === "ventes" && <VentesClient dossierId={dossierId} />}
        {actif.cle === "equipe" && <Collaborateurs />}
        {actif.cle === "transmissions" && <TransmissionsClient dossierId={dossierId} />}
      </section>
    </main>
  );
}

function Collaborateurs() {
  const { session } = useSession();

  if (session?.role === "delegue")
    return (
      <p className="alerte">
        La gestion des collaborateurs est réservée au gérant du dossier.
      </p>
    );

  return (
    <div>
      <p className="resume">
        Créez un lien d'accès par collaborateur. Chacun reçoit son propre lien, et
        ses saisies portent <strong>sa signature</strong> dans « Validé par ».
      </p>
      <p className="note">
        Un collaborateur délégué accède aux documents et au journal, peut
        transmettre, mais ne gère pas les autres collaborateurs.
      </p>
    </div>
  );
}

function TransmissionsClient({ dossierId }: { dossierId: string }) {
  const q = useApi(
    ["transmissions-client", dossierId],
    `/dossiers/${dossierId}/transmissions`,
    TransmissionsSchema,
  );

  const [enCours, setEnCours] = useState(false);

  if (q.isLoading) return <EtatChargement quoi="des transmissions" />;

  return (
    <div>
      <p className="resume">
        Transmettre scelle un instantané de votre dossier : empreinte de chaque
        pièce, copie archivée, procès-verbal. C'est votre preuve de remise.
      </p>

      <button type="button" disabled={enCours} onClick={() => setEnCours(true)}>
        Transmettre le mois en cours
      </button>

      <Tableau
        colonnes={[
          {
            cle: "p",
            titre: "Période",
            rendu: (t) => `${String(t.mois).padStart(2, "0")}/${t.annee}`,
          },
          { cle: "d", titre: "Scellée le", rendu: (t) => t.scelleeLe.slice(0, 10) },
          { cle: "n", titre: "Pièces", montant: true, rendu: (t) => t.nbPieces },
          {
            cle: "pv",
            titre: "Procès-verbal",
            rendu: (t) => (
              <a href={`/api/dossiers/${dossierId}/transmissions/${t.id}/pv`}>Télécharger</a>
            ),
          },
        ]}
        lignes={q.data ?? []}
        cle={(t) => t.id}
        vide="Aucune transmission."
      />
    </div>
  );
}
